import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize'
import { cn } from '@/lib/utils'

interface MarkdownContentProps {
  value: string
  /** Override the prose color scheme. Defaults to `prose-sm` for inline / step description use. */
  className?: string
}

/**
 * Phase 11b: hand-rolled rehype-sanitize schema matching the controlled whitelist documented in
 * LINGO.md → Markdown. The load-bearing XSS gate (server-side sanitization was dropped during
 * Phase 11b build because OWASP Java HTML Sanitizer corrupts Markdown source — see
 * PHASE11_STATUS.md drift note).
 *
 * <p>Whitelist (paired with the disallow rules baked into rehype-sanitize's default schema):
 *
 * <ul>
 *   <li>Allowed elements: h2, h3, h4, p, strong, em, del, a, code, pre, blockquote, ul, ol, li,
 *       hr, table/thead/tbody/tr/th/td, input (only `type="checkbox" disabled` for task lists).
 *   <li>Allowed attributes: a:href, code:className, td:colSpan/rowSpan, input:type/checked/disabled.
 *   <li>Disallow: h1/h5/h6, script, iframe, style, on* event handlers, raw HTML pass-through,
 *       data:/javascript: URLs, embeds, video/audio.
 *   <li>Links open in new tab with rel="nofollow noopener" injected client-side.
 * </ul>
 */
const schema = {
  ...defaultSchema,
  tagNames: [
    'p',
    'strong',
    'em',
    'del',
    'a',
    'code',
    'pre',
    'blockquote',
    'ul',
    'ol',
    'li',
    'hr',
    'h2',
    'h3',
    'h4',
    'table',
    'thead',
    'tbody',
    'tr',
    'th',
    'td',
    'input', // task list checkboxes only — guarded by attribute schema below
  ],
  attributes: {
    a: ['href'],
    code: ['className'],
    td: ['colSpan', 'rowSpan'],
    th: ['colSpan', 'rowSpan'],
    // Task-list checkboxes from remark-gfm: `<input type="checkbox" checked disabled>` only.
    input: [['type', 'checkbox'], ['disabled'], ['checked']],
  },
  protocols: {
    href: ['http', 'https', 'mailto'],
  },
}

export const MarkdownContent = ({ value, className }: MarkdownContentProps) => (
  <div
    className={cn(
      'prose prose-sm max-w-none break-words text-foreground',
      'prose-headings:font-semibold prose-h2:text-base prose-h3:text-sm prose-h4:text-sm',
      'prose-p:my-1 prose-pre:my-2 prose-blockquote:my-2 prose-ul:my-1 prose-ol:my-1',
      'prose-a:text-primary prose-a:underline prose-code:text-foreground',
      className
    )}
  >
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, schema]]}
      components={{
        a: ({ children, ...props }) => (
          <a {...props} target="_blank" rel="nofollow noopener noreferrer">
            {children}
          </a>
        ),
      }}
    >
      {value}
    </ReactMarkdown>
  </div>
)
